import {useEffect} from 'react';

import {QueryParamsForTaggingEnum} from 'presentation/constants';
import {useAccountInformation, useQueryParams} from '../../../hooks';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {UseQueryParamsSyncProps} from './types';

export function useQueryParamsSync({isFirstVisit = false}: UseQueryParamsSyncProps = {}) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {
		beneficiary: {cdUsuarioC},
	} = useAccountInformation();

	const {
		cityName,
		dateAt,
		hourAt,
		professionalCode,
		professionalName,
		specialtyCode,
		specialtyName,
		ufCode,
		ufName,
		unitAddress,
		unitCode,
		unitName,
		bookingNumber,
		districtName,
		isRebooking,
		multipleBookings,
		odontoRecommendations,
		protocolNumber,
		subSpecialtyCode,
		bookingType,
		bookingOrigin,
		bookingRedirect,
	} = bookingData;
	const {setSearchParams, clearSearch} = useQueryParams();

	const activeBookingFlowStep = useBookingFlowStepsStore(state => state.bookingStep);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);

	useEffect(() => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsForTaggingEnum.STEP, (activeBookingFlowStep + 1).toString());
				prev.set(QueryParamsForTaggingEnum.FIRST_VISIT, isFirstVisit ? 'true' : 'false');

				if (bookingRedirect) {
					prev.set(QueryParamsForTaggingEnum.BOOKING_REDIRECT, String(bookingRedirect));
				} else {
					prev.delete(QueryParamsForTaggingEnum.BOOKING_REDIRECT);
				}

				if (bookingOrigin) {
					prev.set(QueryParamsForTaggingEnum.BOOKING_ORIGIN, bookingOrigin);
				} else {
					prev.delete(QueryParamsForTaggingEnum.BOOKING_ORIGIN);
				}

				if (bookingType) {
					prev.set(QueryParamsForTaggingEnum.BOOKING_TYPE, bookingType);
				} else {
					prev.delete(QueryParamsForTaggingEnum.BOOKING_TYPE);
				}

				if (specialtyCode) {
					prev.set(QueryParamsForTaggingEnum.SPECIALTY_CODE, specialtyCode);
				} else {
					prev.delete(QueryParamsForTaggingEnum.SPECIALTY_CODE);
				}

				if (specialtyName) {
					prev.set(QueryParamsForTaggingEnum.SPECIALTY_NAME, specialtyName);
				} else {
					prev.delete(QueryParamsForTaggingEnum.SPECIALTY_NAME);
				}

				if (unitCode) {
					prev.set(QueryParamsForTaggingEnum.UNIT_CODE, unitCode);
				} else {
					prev.delete(QueryParamsForTaggingEnum.UNIT_CODE);
				}

				if (unitName) {
					prev.set(QueryParamsForTaggingEnum.UNIT_NAME, unitName);
				} else {
					prev.delete(QueryParamsForTaggingEnum.UNIT_NAME);
				}

				if (unitAddress) {
					prev.set(QueryParamsForTaggingEnum.UNIT_ADDRESS, unitAddress);
				} else {
					prev.delete(QueryParamsForTaggingEnum.UNIT_ADDRESS);
				}

				if (professionalCode) {
					prev.set(QueryParamsForTaggingEnum.PROFESSIONAL_CODE, professionalCode);
				} else {
					prev.delete(QueryParamsForTaggingEnum.PROFESSIONAL_CODE);
				}

				if (professionalName) {
					prev.set(QueryParamsForTaggingEnum.PROFESSIONAL_NAME, professionalName);
				} else {
					prev.delete(QueryParamsForTaggingEnum.PROFESSIONAL_NAME);
				}

				if (dateAt) {
					prev.set(QueryParamsForTaggingEnum.DATE_AT, dateAt);
				} else {
					prev.delete(QueryParamsForTaggingEnum.DATE_AT);
				}

				if (hourAt) {
					prev.set(QueryParamsForTaggingEnum.HOUR_AT, hourAt);
				} else {
					prev.delete(QueryParamsForTaggingEnum.HOUR_AT);
				}

				if (ufCode) {
					prev.set(QueryParamsForTaggingEnum.UF_CODE, ufCode);
				} else {
					prev.delete(QueryParamsForTaggingEnum.UF_CODE);
				}

				if (ufName) {
					prev.set(QueryParamsForTaggingEnum.UF_NAME, ufName.trim().toUpperCase());
				} else {
					prev.delete(QueryParamsForTaggingEnum.UF_NAME);
				}

				if (cityName) {
					prev.set(QueryParamsForTaggingEnum.CITY_NAME, cityName.trim().toUpperCase());
				} else {
					prev.delete(QueryParamsForTaggingEnum.CITY_NAME);
				}

				if (districtName) {
					prev.set(QueryParamsForTaggingEnum.DISTRICT_NAME, districtName);
				} else {
					prev.delete(QueryParamsForTaggingEnum.DISTRICT_NAME);
				}

				if (subSpecialtyCode) {
					prev.set(QueryParamsForTaggingEnum.SUB_SPECIALTY_CODE, subSpecialtyCode);
				} else {
					prev.delete(QueryParamsForTaggingEnum.SUB_SPECIALTY_CODE);
				}

				if (isRebooking) {
					prev.set(QueryParamsForTaggingEnum.IS_REBOOKING, String(isRebooking));
				} else {
					prev.delete(QueryParamsForTaggingEnum.IS_REBOOKING);
				}

				if (bookingNumber) {
					prev.set(QueryParamsForTaggingEnum.BOOKING_NUMBER, bookingNumber);
				} else {
					prev.delete(QueryParamsForTaggingEnum.BOOKING_NUMBER);
				}

				if (protocolNumber) {
					prev.set(QueryParamsForTaggingEnum.PROTOCOL_NUMBER, protocolNumber);
				} else {
					prev.delete(QueryParamsForTaggingEnum.PROTOCOL_NUMBER);
				}

				if (odontoRecommendations) {
					prev.set(
						QueryParamsForTaggingEnum.ODONTO_RECOMMENDATIONS,
						odontoRecommendations,
					);
				} else {
					prev.delete(QueryParamsForTaggingEnum.ODONTO_RECOMMENDATIONS);
				}

				if (multipleBookings) {
					prev.set(QueryParamsForTaggingEnum.MULTIPLE_BOOKINGS, String(multipleBookings));
				} else {
					prev.delete(QueryParamsForTaggingEnum.MULTIPLE_BOOKINGS);
				}

				if (cdUsuarioC) {
					prev.set(QueryParamsForTaggingEnum.BENEFICIARY_CARD, cdUsuarioC);
				} else {
					prev.delete(QueryParamsForTaggingEnum.BENEFICIARY_CARD);
				}

				return prev;
			},
			{
				replace: true,
			},
		);
	}, [
		activeBookingFlowStep,
		isFirstVisit,
		cityName,
		dateAt,
		hourAt,
		professionalCode,
		professionalName,
		specialtyCode,
		specialtyName,
		ufCode,
		ufName,
		unitAddress,
		unitCode,
		unitName,
		bookingNumber,
		districtName,
		isRebooking,
		multipleBookings,
		odontoRecommendations,
		protocolNumber,
		subSpecialtyCode,
		cdUsuarioC,
	]);

	return {
		setBookingData,
		bookingData,
		setActiveBookingFlowStep,
		activeBookingFlowStep,
		setSearchParams,
		clearSearch,
	};
}
